import { FormControl, useToast } from "@chakra-ui/react";
import { format } from "date-fns";
import { fr } from "date-fns/locale/fr";
import FormField from "../../../components/fields/FormField";
import FormModal from "../../../components/FormModal";
import { translate } from "../../../i18n";
import { CourtAvailabilityDto } from "../../../models/court/dto/court-availability.dto";
import { buildCapacityPricingLossWarning } from "../../../utils/capacity-pricing-warnings";
import { buildAvailabilityWriteWarning, buildCommitmentCoverageLossWarning } from "../../../utils/commitment-reprice-warnings";
import { useDeleteCourtAvailability } from "../hooks/useDeleteCourtAvailability";
import { useIsOrgManager } from "../../auth/roles";
type Props = {
    isModalVisible: boolean;
    setIsModalVisible: (newValue: boolean) => void;
    onCourtAvailabilityDeleted: (id: string) => void;
    selectedAvailability: CourtAvailabilityDto;
};
export default function SelectedCourtAvailabilityModal({ selectedAvailability, isModalVisible, setIsModalVisible, onCourtAvailabilityDeleted }: Props) {
    const toast = useToast();
    const { mutateAsync: deleteAvailability, isLoading: isDeleting } = useDeleteCourtAvailability((availabilityId, result) => {
        onCourtAvailabilityDeleted(availabilityId);
        setIsModalVisible(false);
        // The API refuses this delete outright when an upcoming game would lose
        // its price AT ITS CAPACITY while the slot keeps selling at other sizes;
        // what it cannot refuse is a window whose pricing rows were the venue's
        // LAST for a size nobody has booked yet, which stops that size selling
        // everywhere with no booking to point at (D136) — or one whose rows were
        // merely winning over another window's, which leaves every upcoming
        // booking priceable at the OTHER window's amount — or the last window
        // over a booked slot, which leaves those bookings with no price at all
        // (D165).
        // Toasted after the modal closes, so the message is not dismissed with
        // it.
        const warning = buildAvailabilityWriteWarning(result);
        if (warning) {
            toast({
                // The title names the STRONGEST thing in the message, on the
                // same ordering the message body uses: a booking nobody can
                // join, then a size that stopped selling, then a price that
                // merely moved. A delete that only repriced must not be
                // announced as one that took a size off sale, and neither must
                // be announced as one that left a sold booking unpriceable.
                title: translate(
                    buildCommitmentCoverageLossWarning(result)
                        ? 'courts.commitments_unpriced_title'
                        : buildCapacityPricingLossWarning(result)
                          ? 'courts.capacity_pricing_lost_title'
                          : 'courts.commitments_repriced_title'
                ),
                description: warning,
                status: 'warning',
                duration: 10000,
                isClosable: true,
                position: 'top-right',
            });
        }
    });
    // `DELETE courts/availabilities/:id` is hasOrganizationAdminAccess on the
    // API. Both calendars that open this modal are coach-reachable and route
    // EVERY click through their `onEventSelected` with no role check (court
    // availabilities are react-big-calendar `backgroundEvents`, which do fire
    // onSelectEvent), so without this gate a coach got a Supprimer button that
    // 403s.
    const isOrgManager = useIsOrgManager();

    if (!selectedAvailability) return null;

    return (
        <FormModal
            isOpen={isModalVisible}
            onClose={() => setIsModalVisible(false)}
            title={translate('courts.availabilities')}
            size="3xl"
            submitLabel={translate('close')}
            onSubmit={() => setIsModalVisible(false)}
            destructiveAction={
                isOrgManager
                    ? {
                          label: translate('delete'),
                          // `mutateAsync` rejects on a 409 (the priceability
                          // guard) and nothing was catching it: every blocked
                          // delete raised an unhandled rejection on top of
                          // showing the operator nothing. The hook's `onError`
                          // owns the message; this only stops the throw.
                          onClick: () => {
                              void deleteAvailability(selectedAvailability.id).catch(() => undefined);
                          },
                          isLoading: isDeleting,
                      }
                    : undefined
            }
        >
                    <FormControl>
                        <FormField
                            label={translate('event.start_date')}
                            value={format(new Date(selectedAvailability.startTime), "dd MMMM yyyy HH:mm", { locale: fr })}
                            isDisabled
                            onChange={() => {}}
                        />
                        <FormField
                            label={translate('event.end_date')}
                            value={format(new Date(selectedAvailability.endTime), "dd MMMM yyyy HH:mm", { locale: fr })}
                            isDisabled
                            onChange={() => {}}
                        />
                    </FormControl>
        </FormModal>
    )
}
